import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import axios from 'axios'

import Navbar from '../Navbar'

import {
  FiUser,
  FiMail,
  FiPhone,
  FiShield,
  FiEdit3,
  FiCheckCircle,
  FiKey
} from 'react-icons/fi'


export default function Profile() {

  const { currentUser, url } = useSelector(
    state => state
  )

  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [sendingEmailLink, setSendingEmailLink] =
    useState(false)


  const sendResetLink = async () => {

    try {

      setSendingEmailLink(true)
      setMessage('')
      setError('')

      const response = await axios.post(
        `${url}/auth/email-confirmation-link`,
        {},
        {
          headers: {
            Authorization:
              `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      if (response.data.message) {

        setMessage(
          response.data.message
        )

      } else if (response.data.error) {

        setError(
          response.data.error
        )

      }

    } catch (err) {

      console.error(
        'Email update error:',
        err
      )

      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to send the email update link.'
      )

    } finally {

      setSendingEmailLink(false)
    }
  }


  const initials =
    getInitials(
      currentUser?.fullname
    )


  return (

    <div className="
      min-h-screen
      bg-[#F7F9FC]
      font-[Satoshi]
      text-gray-900
    ">

      <Navbar />


<main className="
  lg:ml-[15em]
  min-w-0
  min-h-screen
  flex-1
  bg-[#F7F9FC]
  px-5
  pb-10
  pt-24
  sm:px-6
  lg:px-8
  lg:pt-8
">

  <div className="
    mx-auto
    max-w-[1500px]
  ">

          {/* PAGE HEADER */}

          <div className="
            mb-6
            sm:mb-8
          ">

            <p className="
              text-sm
              font-semibold
              text-green-600
            ">
              ACCOUNT
            </p>

            <h1 className="
              mt-1
              text-2xl
              sm:text-3xl
              font-bold
              tracking-tight
              text-gray-900
            ">
              Your profile
            </h1>

            <p className="
              mt-2
              text-sm
              leading-6
              text-gray-500
            ">
              Manage your personal information and RentHub account details.
            </p>

          </div>


          {/* FEEDBACK */}

          {message && (

            <div className="
              mb-6
              flex
              items-start
              gap-3
              bg-green-50
              border
              border-green-200
              text-green-700
              rounded-xl
              px-5
              py-4
              text-sm
            ">

              <FiCheckCircle
                className="
                  text-lg
                  mt-0.5
                  shrink-0
                "
              />

              <p>
                {message}
              </p>

            </div>

          )}


          {error && (

            <div className="
              mb-6
              bg-red-50
              border
              border-red-200
              text-red-600
              rounded-xl
              px-5
              py-4
              text-sm
            ">
              {error}
            </div>

          )}


          <div className="
            grid
            grid-cols-1
            lg:grid-cols-[320px_minmax(0,1fr)]
            gap-6
            items-start
          ">


            {/* PROFILE CARD */}

            <aside className="
              bg-[#101828]
              text-white
              rounded-3xl
              p-7
              shadow-sm
              overflow-hidden
              relative
            ">

              <div className="
                absolute
                w-56
                h-56
                bg-green-500/10
                rounded-full
                -right-20
                -top-24
              " />


              <div className="relative">

                <div className="
                  w-20
                  h-20
                  rounded-2xl
                  bg-green-500
                  flex
                  items-center
                  justify-center
                  text-2xl
                  font-bold
                  mb-5
                ">
                  {initials}
                </div>


                <h2 className="
                  text-xl
                  font-bold
                  break-words
                ">
                  {currentUser?.fullname ||
                    'RentHub Customer'}
                </h2>


                <p className="
                  text-gray-400
                  text-sm
                  mt-1
                  break-all
                ">
                  {currentUser?.email || '—'}
                </p>


                <div className="
                  mt-6
                  pt-6
                  border-t
                  border-white/10
                ">

                  <div className="
                    inline-flex
                    items-center
                    gap-2
                    bg-green-500/10
                    text-green-300
                    text-xs
                    font-bold
                    px-3
                    py-2
                    rounded-full
                  ">

                    <FiShield />

                    RENTHUB CUSTOMER

                  </div>

                </div>


                <p className="
                  text-xs
                  text-gray-500
                  leading-5
                  mt-6
                ">
                  Keep your contact information up to date so RentHub can reach you about your loan and repayments.
                </p>

              </div>

            </aside>


            {/* ACCOUNT DETAILS */}

            <div className="space-y-6">


              {/* PERSONAL INFORMATION */}

              <section className="
                bg-white
                border
                border-gray-200
                rounded-2xl
                shadow-sm
                overflow-hidden
              ">

                <div className="
                  px-6
                  py-5
                  border-b
                  border-gray-100
                ">

                  <h2 className="
                    text-lg
                    font-bold
                  ">
                    Personal information
                  </h2>

                  <p className="
                    text-sm
                    text-gray-500
                    mt-1
                  ">
                    Your basic RentHub account details.
                  </p>

                </div>


                <div className="
                  divide-y
                  divide-gray-100
                ">


                  {/* FULL NAME */}

                  <ProfileRow
                    icon={<FiUser />}
                    label="Full name"
                    value={
                      currentUser?.fullname ||
                      '—'
                    }
                    action={

                      <Link
                        to="/dashboard/profile/edit_fullname"
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          text-sm
                          font-semibold
                          text-green-600
                          hover:text-green-700
                        "
                      >

                        <FiEdit3 />

                        Edit

                      </Link>

                    }
                  />


                  {/* EMAIL */}

                  <ProfileRow
                    icon={<FiMail />}
                    label="Email address"
                    value={
                      currentUser?.email ||
                      '—'
                    }
                    action={

                      <button
                        type="button"
                        onClick={sendResetLink}
                        disabled={
                          sendingEmailLink
                        }
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          text-sm
                          font-semibold
                          text-green-600
                          hover:text-green-700
                          disabled:text-gray-400
                          disabled:cursor-not-allowed
                        "
                      >

                        <FiEdit3 />

                        {
                          sendingEmailLink
                            ? 'Sending...'
                            : 'Change'
                        }

                      </button>

                    }
                  />


                  {/* PHONE */}

                  <ProfileRow
                    icon={<FiPhone />}
                    label="Phone number"
                    value={
                      currentUser?.phone_number ||
                      '—'
                    }
                    action={

                      <Link
                        to="/dashboard/profile/edit_phone_number"
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          text-sm
                          font-semibold
                          text-green-600
                          hover:text-green-700
                        "
                      >

                        <FiEdit3 />

                        Edit

                      </Link>

                    }
                  />

                </div>

              </section>


              {/* IDENTITY */}

              <section className="
                bg-white
                border
                border-gray-200
                rounded-2xl
                shadow-sm
                overflow-hidden
              ">

                <div className="
                  px-6
                  py-5
                  border-b
                  border-gray-100
                ">

                  <h2 className="
                    text-lg
                    font-bold
                  ">
                    Identity verification
                  </h2>

                  <p className="
                    text-sm
                    text-gray-500
                    mt-1
                  ">
                    Identity information connected to your RentHub account.
                  </p>

                </div>


                <div className="p-6">

                  <div className="
                    flex
                    flex-col
                    sm:flex-row
                    sm:items-center
                    sm:justify-between
                    gap-5
                    bg-gray-50
                    rounded-2xl
                    p-5
                  ">

                    <div className="
                      flex
                      items-center
                      gap-4
                    ">

                      <div className="
                        w-11
                        h-11
                        shrink-0
                        rounded-xl
                        bg-green-50
                        text-green-600
                        flex
                        items-center
                        justify-center
                        text-xl
                      ">
                        <FiShield />
                      </div>


                      <div>

                        <p className="
                          text-xs
                          uppercase
                          tracking-wide
                          font-semibold
                          text-gray-400
                        ">
                          Bank Verification Number
                        </p>

                        <p className="
                          font-mono
                          font-semibold
                          text-gray-800
                          mt-1
                        ">
                          {maskBVN(
                            currentUser?.bvn
                          )}
                        </p>

                      </div>

                    </div>


                    {currentUser?.bvn && (

                      <div className="
                        inline-flex
                        items-center
                        gap-1.5
                        text-xs
                        font-bold
                        text-green-700
                        bg-green-100
                        px-3
                        py-2
                        rounded-full
                        w-fit
                      ">

                        <FiCheckCircle />

                        ON FILE

                      </div>

                    )}

                  </div>


                  <p className="
                    text-xs
                    text-gray-400
                    leading-5
                    mt-4
                  ">
                    Your BVN is partially hidden for security and cannot be edited from your profile.
                  </p>

                </div>

              </section>


              {/* SECURITY */}

              <section className="
                bg-white
                border
                border-gray-200
                rounded-2xl
                shadow-sm
                p-6
              ">

                <div className="
                  flex
                  flex-col
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                  gap-5
                ">

                  <div className="
                    flex
                    gap-4
                    items-start
                  ">

                    <div className="
                      w-11
                      h-11
                      shrink-0
                      rounded-xl
                      bg-gray-100
                      text-gray-600
                      flex
                      items-center
                      justify-center
                      text-xl
                    ">
                      <FiKey />
                    </div>


                    <div>

                      <p className="
                        font-bold
                        text-gray-900
                      ">
                        Password & security
                      </p>

                      <p className="
                        text-sm
                        text-gray-500
                        mt-1
                      ">
                        Use a strong password and keep your RentHub account secure.
                      </p>

                    </div>

                  </div>


                  <Link
                    to="/Auth/forgot_password/"
                    className="
                      border
                      border-gray-300
                      hover:border-gray-400
                      hover:bg-gray-50
                      text-gray-700
                      text-sm
                      font-semibold
                      px-4
                      py-2.5
                      rounded-xl
                      transition
                      text-center
                      shrink-0
                    "
                  >
                    Change password
                  </Link>

                </div>

              </section>

            </div>

          </div>

        </div>

      </main>

    </div>
  )
}



function ProfileRow({
  icon,
  label,
  value,
  action
}) {

  return (

    <div className="
      flex
      flex-col
      sm:flex-row
      sm:items-center
      sm:justify-between
      gap-4
      px-6
      py-5
    ">

      <div className="
        flex
        items-center
        gap-4
        min-w-0
      ">

        <div className="
          w-10
          h-10
          shrink-0
          rounded-xl
          bg-gray-50
          text-gray-500
          flex
          items-center
          justify-center
        ">
          {icon}
        </div>


        <div className="min-w-0">

          <p className="
            text-xs
            uppercase
            tracking-wide
            font-semibold
            text-gray-400
          ">
            {label}
          </p>

          <p className="
            text-sm
            font-semibold
            text-gray-900
            mt-1
            break-words
          ">
            {value}
          </p>

        </div>

      </div>


      <div className="
        ml-14
        sm:ml-0
        shrink-0
      ">
        {action}
      </div>

    </div>
  )
}



function getInitials(name) {

  if (!name) {
    return 'RH'
  }

  const parts =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)

  if (parts.length === 1) {
    return parts[0]
      .slice(0, 2)
      .toUpperCase()
  }

  return (
    parts[0][0] +
    parts[parts.length - 1][0]
  ).toUpperCase()
}



function maskBVN(bvn) {

  if (!bvn) {
    return 'Not available'
  }

  const value =
    String(bvn)

  if (value.length <= 4) {
    return value
  }

  return `${'*'.repeat(
    value.length - 4
  )}${value.slice(-4)}`
}